import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityKindEnum, ExecutionDetailOutDTO, RobotActivityItemOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { usePushEvent } from '@hooks/usePush/usePushEvent';
import { PushContext } from '@hooks/usePush/PushContext';
import { dayjsFromApiUtcInTz, formatDateInTzWithLocale, stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import EntityLink from '../EntityLink';
import ExplainTree from '../ExplainTree';
import { actionDetail, actionError } from '../i18nKeys';
import { useRuleApi } from '../useRuleApi';
import { FeedGroup, GroupState, groupFeedItems, groupState } from './feedGrouping';
import { useTriggerLabel } from './useTriggerLabel';

interface ComponentProps {
  companyId?: string;
  /** Counters are refreshed on the same beat as the feed, so one poll drives both. */
  onTick?: () => void;
}

const FEED_LIMIT = 50;
/** No push: the only way to notice anything is to ask. */
const POLL_MS = 5000;
/** Push connected: hints carry the news, so this is just insurance against a lost one. */
const PUSH_HEARTBEAT_MS = 30000;
const MAX_ITEMS = 200;

/**
 * What the robot has done, newest first — one card per run over one expedition.
 *
 * The feed arrives as a flat list of rows: an event being queued, a row for every rule put to it,
 * the event finishing. Read flat, rows from different expeditions interleave and the reader has to
 * reassemble the story themselves. `feedGrouping.ts` does that reassembly, so a card answers
 * *what did the robot do about this shipment* rather than *what row was written at 14:32:07*.
 *
 * Polling is the baseline and push is only ever "poll now": push is disabled outright on some
 * environments, and an admin watching another company gets no push because the connection token
 * carries the logged-in user's own company.
 */
const ActivityFeed: React.FC<ComponentProps> = ({ companyId, onTick }) => {
  const { tr } = useTranslationLgs();
  const { activityFeed } = useRuleApi(companyId);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { connectionState } = useContext(PushContext);
  const pushLive = connectionState === 'connected' && !companyId;

  const [items, setItems] = useState<RobotActivityItemOutDTO[]>([]);
  const [loaded, setLoaded] = useState(false);
  const newestAt = useRef<string>(undefined);
  // `poll` refers to itself when draining a burst; a ref keeps that from needing a forward
  // declaration and keeps the callback identity stable.
  const pollRef = useRef<() => void>(() => undefined);

  const poll = useCallback(() => {
    activityFeed(
      newestAt.current,
      FEED_LIMIT,
      response => {
        setLoaded(true);
        const fresh = response?.items ?? [];
        if (fresh.length === 0) return;

        // The cursor comes from the response: a cold start is newest-first but a cursored request
        // walks forward, so neither end of `items` is reliably the newest.
        newestAt.current = response.nextSince ?? fresh.reduce((newest, item) => (item.at > newest ? item.at : newest), fresh[0].at);

        setItems(previous => {
          const known = new Set(previous.map(item => item.id));
          const added = fresh.filter(item => !known.has(item.id));
          if (added.length === 0) return previous;
          // Merge, then sort by time rather than by arrival: history and live rows are the same
          // list and have to read as one.
          return [...added, ...previous].sort((left, right) => right.at.localeCompare(left.at)).slice(0, MAX_ITEMS);
        });
        onTick?.();

        if (response.hasMore) setTimeout(() => pollRef.current(), 0);
      },
      // A failed poll must not raise a toast every few seconds.
      { onError: () => setLoaded(true) },
    );
  }, [activityFeed, onTick]);
  pollRef.current = poll;

  useEffect(() => {
    poll();
    const handle = setInterval(poll, pushLive ? PUSH_HEARTBEAT_MS : POLL_MS);
    return () => clearInterval(handle);
  }, [poll, pushLive]);

  // The push payload is a hint, never data — react by polling, so a lost or reordered message
  // costs nothing.
  usePushEvent(() => poll(), { types: ['ai-dispatcher.execution'], onReconnect: () => poll() });

  // Every run is shown, in order: the grouping is what makes the volume readable, so there is
  // nothing here to filter out. A run where every rule declined folds itself down to one line.
  const groups = useMemo(() => groupFeedItems(items), [items]);

  const header = (
    <div className="flex items-center gap-2 text-xs text-content-tertiary">
      <span className={`inline-block h-2 w-2 rounded-full ${pushLive ? 'bg-green-500' : 'bg-content-tertiary'}`} />
      <span>{pushLive ? tr('AiDispatcher.feedLive', 'Live') : tr('AiDispatcher.feedPolling', 'Refreshing every few seconds')}</span>
    </div>
  );

  if (loaded && groups.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {header}
        <div className="rounded-md border border-dashed border-base-tertiary p-6 text-center text-content-secondary">
          {tr('AiDispatcher.feedEmpty', 'Nothing yet. The robot writes here as soon as something happens.')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {header}

      {groups.map((group, index) => {
        const previous = groups[index - 1];
        const day = formatDateInTzWithLocale(group.at, loggedUser?.defaultTimezoneId);
        const newDay = !previous || formatDateInTzWithLocale(previous.at, loggedUser?.defaultTimezoneId) !== day;
        return (
          <React.Fragment key={group.id}>
            {/* Dates as a separator rather than on every row: a feed is read as "today, then
                earlier", and the rows themselves only need a time. */}
            {newDay && <div className="mt-2 text-xs font-medium uppercase tracking-wide text-content-tertiary">{day}</div>}
            <GroupCard group={group} timezoneId={loggedUser?.defaultTimezoneId} companyId={companyId} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

/** How a whole run reads at a glance. Colour carries the same meaning as the counters above. */
const useStatePresentation = (state: GroupState) => {
  const { tr } = useTranslationLgs();

  switch (state) {
    case 'FAILED':
      return { label: tr('AiDispatcher.groupFailed', 'Failed'), chip: 'bg-red-100 text-red-800', border: 'border-red-200 bg-red-50' };
    case 'ACTED':
      return { label: tr('AiDispatcher.groupActed', 'Acted'), chip: 'bg-green-100 text-green-800', border: 'border-base-tertiary bg-white' };
    case 'RUNNING':
      return { label: tr('AiDispatcher.groupRunning', 'In progress'), chip: 'bg-amber-100 text-amber-800', border: 'border-base-tertiary bg-white' };
    case 'NO_MATCH':
      // Not a failure: every rule looked and decided no.
      return { label: tr('AiDispatcher.groupNoMatch', 'No match'), chip: 'bg-base-tertiary text-content-secondary', border: 'border-base-tertiary bg-white' };
    case 'DONE':
    default:
      return { label: tr('AiDispatcher.groupDone', 'Done'), chip: 'bg-base-tertiary text-content-secondary', border: 'border-base-tertiary bg-white' };
  }
};

/**
 * One run over one expedition.
 *
 * Reads top to bottom as *what happened · to what · when*, then *why it ran*, then one line per
 * rule that did something. The rules that declined are a single folded line: they are the answer
 * to "why didn't my rule fire", which is a question asked occasionally, not a thing to read past
 * every time.
 */
const GroupCard: React.FC<{ group: FeedGroup; timezoneId?: string; companyId?: string }> = ({ group, timezoneId, companyId }) => {
  const { tr } = useTranslationLgs();
  const triggerLabel = useTriggerLabel();
  const state = useStatePresentation(groupState(group));
  const [showSkipped, setShowSkipped] = useState(false);

  const acted = group.rules.filter(rule => rule.activityKind !== ActivityKindEnum.RULE_SKIPPED);
  const skipped = group.rules.filter(rule => rule.activityKind === ActivityKindEnum.RULE_SKIPPED);
  const time = dayjsFromApiUtcInTz(group.at, timezoneId)?.format('LT');

  return (
    <section className={`flex flex-col gap-2 rounded-md border border-solid p-3 ${state.border}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${state.chip}`}>{state.label}</span>
        <EntityLink entityType={group.entityType} entityId={group.entityId} entityLabel={group.entityLabel} />
        <span className="ml-auto text-xs text-content-tertiary" title={stringDateTimeWithLocaleFromApiStringInTz(group.at, timezoneId)}>
          {time}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-content-secondary">
        {group.triggerType && <span>{triggerLabel(group.triggerType)}</span>}
        {group.ruleCount !== null && group.ruleCount !== undefined && (
          <>
            <span className="text-content-tertiary">·</span>
            <span>{tr('AiDispatcher.ruleCount', '{{count}} rule(s) to consider', { count: group.ruleCount })}</span>
          </>
        )}
        {/* Still in the future: the answer to "why hasn't it done anything yet". */}
        {group.notBefore && new Date(group.notBefore).getTime() > Date.now() && (
          <>
            <span className="text-content-tertiary">·</span>
            <span className="text-amber-700">
              {tr('AiDispatcher.notBeforeAt', 'Runs at {{at}}', { at: stringDateTimeWithLocaleFromApiStringInTz(group.notBefore, timezoneId) })}
            </span>
          </>
        )}
      </div>

      {acted.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {acted.map(rule => (
            <RuleLine key={rule.id} rule={rule} companyId={companyId} />
          ))}
        </ul>
      )}

      {skipped.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-content-tertiary">{tr('AiDispatcher.groupSkippedCount', '{{count}} rule(s) did not match', { count: skipped.length })}</span>
            <RingilButton
              mode="text-only"
              label={showSkipped ? tr('AiDispatcher.hideLabel', 'Hide') : tr('AiDispatcher.showLabel', 'Show')}
              onClick={() => setShowSkipped(!showSkipped)}
            />
          </div>
          {showSkipped && (
            <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm">
              {skipped.map(rule => (
                <li key={rule.id} className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-content-tertiary">{rule.ruleName ?? tr('AiDispatcher.deletedRule', 'Deleted rule')}</span>
                    {/* "Did not match" on its own is not a reason. The trace names the condition
                        that decided it, with the value the rule wanted and the value it found. */}
                    <WhyExplain executionId={rule.id} companyId={companyId} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {group.error && <div className="text-sm text-red-700">{group.error}</div>}
    </section>
  );
};

/** One rule that did something, and what it did. */
const RuleLine: React.FC<{ rule: RobotActivityItemOutDTO; companyId?: string }> = ({ rule, companyId }) => {
  const { tr } = useTranslationLgs();
  const failed = rule.activityKind === ActivityKindEnum.RULE_FAILED;

  return (
    <li className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={failed ? 'text-red-700' : 'text-green-700'}>{failed ? '✕' : '✓'}</span>
        <span className="font-medium">{rule.ruleName ?? tr('AiDispatcher.deletedRule', 'Deleted rule')}</span>
        {rule.ruleDeleted && rule.ruleName && <span className="text-xs text-content-tertiary">{tr('AiDispatcher.ruleSinceDeleted', '(since deleted)')}</span>}
        <WhyExplain executionId={rule.id} companyId={companyId} />
      </div>

      {rule.actions.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-0.5 p-0 pl-5 text-sm">
          {rule.actions.map((action, index) => {
            const detail = actionDetail(tr, action);
            const error = actionError(tr, action);
            return (
              <li key={index} className={action.ok ? 'text-content-secondary' : 'text-red-700'}>
                {/* The action writes its own summary, so a line renders without the frontend
                    knowing what any particular action does. */}
                {detail ?? action.action}
                {error && <span className="text-red-700"> — {error}</span>}
              </li>
            );
          })}
        </ul>
      )}

      {rule.error && <div className="pl-5 text-sm text-red-700">{rule.error}</div>}
    </li>
  );
};

/**
 * The condition tree behind one execution, on demand.
 *
 * A feed row for a rule *is* an execution — its id is the execution id — so the trace is one call
 * away, and it is the only thing that answers "it didn't match, but against what?": every
 * condition with the value the rule asked for beside the value the expedition actually had.
 *
 * Fetched when it is first opened rather than with the feed: most rows are never asked about, and
 * a trace per row would be a request per row on every poll.
 */
const WhyExplain: React.FC<{ executionId: string; companyId?: string }> = ({ executionId, companyId }) => {
  const { tr } = useTranslationLgs();
  const { getExecution } = useRuleApi(companyId);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<ExecutionDetailOutDTO>();
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    if (!open && !detail) {
      setFailed(false);
      getExecution(executionId, setDetail, { onError: () => setFailed(true) });
    }
    setOpen(!open);
  };

  return (
    <>
      <RingilButton
        mode="text-only"
        label={open ? tr('AiDispatcher.hideLabel', 'Hide') : tr('AiDispatcher.whyLabel', 'Why?')}
        tooltip={tr('AiDispatcher.whyTooltip', 'Show the condition tree with the values it saw')}
        onClick={toggle}
      />
      {open && (
        <div className="w-full pl-5">
          {detail ? (
            <ExplainTree explain={detail.explain} companyId={companyId} />
          ) : (
            <span className="text-sm text-content-tertiary">{failed ? tr('AiDispatcher.explainFailed', 'The trace could not be loaded.') : tr('AiDispatcher.explainLoading', 'Loading…')}</span>
          )}
        </div>
      )}
    </>
  );
};

export default ActivityFeed;
